import React, { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import {
  MenuStrip,
  NavHeader,
  TopNavStrip,
  MobileMenu,
  MobileNavRow,
  AnimatedHeaderWrapper,
} from './Components'
import {
  useMenuAim,
  LocalLabelsProvider,
  useToggleNavbarOnScroll,
  useSearchbar,
  SearchBoxProps,
  OverflowHidden,
  useClickOutside,
  Link,
} from '@sentecacommerce-theme/lib'
import dynamic from 'next/dynamic'
import { useInView } from 'react-intersection-observer'
import { MenuCategories } from './Components/MenuCategories'
import { LazyImage } from '../Common'
import { NavbarProps, BottomMenuEnum } from './navbar.props'
import { BaseMenuItem, Labels } from '../MenuLayout/MenuLayout.props'

const SearchBox = dynamic<SearchBoxProps>(() =>
  import('@sentecacommerce-theme/lib').then((mod) => mod.SearchBox)
)

export const Navbar = (props: NavbarProps) => {
  const {
    menuItems,
    drawerOpen,
    handleOpen,
    drawerToggleHandler,
    isIndex,
    suggestedMenus,
    mainRef,
    labels,
    topStrip,
    searchBox,
  } = props
  const [ref, inView] = useInView({
    threshold: 0.3,
  })

  const containerRef = useRef(null)
  const [scrolled, setScrolled] = useState(false)
  const setScrollOnce = () => (!scrolled ? setScrolled(true) : null)
  const displayNavbar = useToggleNavbarOnScroll()
  const menuCategories = (menuItems?.[0] as BaseMenuItem) ?? ''
  const mainBannerUrl = menuCategories?.menuUrl as string

  useEffect(() => {
    window.addEventListener('scroll', setScrollOnce, { once: true })
    return () => {
      window.removeEventListener('scroll', setScrollOnce)
    }
  }, [])

  const [stripRef, stripInView] = useInView()
  const [desktopNavOpen, setDesktopNavOpen] = useState(false)
  const toggleDesktopNav = (value: boolean) => () => setDesktopNavOpen(value)
  const categoryRef = useRef(null)
  const [value, setValue] = React.useState('')
  const [isSearchBoxOpen, setIsSearchBoxOpen] = useState(false)

  const handleSearchBoxClose = () => {
    setIsSearchBoxOpen(false)
    setValue('')
    searchBarData.clearResult()
  }

  const searchBarData = useSearchbar({
    onSearchSubmitCallback: () => handleSearchBoxClose(),
    limit: searchBox.bottomMenu === BottomMenuEnum.moreProducts ? 9 : 6,
  })
  const handleMouseleave = (
    allowedRef: React.RefObject<any> | React.RefObject<any>[],
    callback: () => any,
    disallowedRef?: React.RefObject<any>
  ) => (event: MouseEvent) => {
    let refs = []
    if (!Array.isArray(allowedRef)) {
      refs = [allowedRef]
    } else {
      refs = allowedRef
    }
    try {
      if (
        (disallowedRef?.current &&
          event.relatedTarget === disallowedRef.current) ||
        refs.every(
          (a) => a?.current && !a.current?.contains(event.relatedTarget)
        )
      ) {
        callback()
      }
    } catch (e) {
      return e
    }
  }

  const categoryItems = (menuItems as BaseMenuItem[]).filter(
    (item) => item.menuClass === 'category'
  )[0]
  const specialItems = (menuItems as BaseMenuItem[]).filter(
    (item) => item.menuClass === 'special' || item.menuClass === 'sale'
  )

  const [active, reset] = useMenuAim(categoryRef)

  const absoluteRef = useRef(null)
  const categoryOutOfView =
    (isIndex && !inView && scrolled) || (!isIndex && !stripInView && scrolled)
  const headerRef = useRef(null)
  useClickOutside(containerRef, () => handleSearchBoxClose(), [containerRef])
  return (
    <LocalLabelsProvider<Labels> labels={labels}>
      <>
        {drawerOpen && (
          <MobileMenu
            menuItems={categoryItems.nodes as BaseMenuItem[]}
            specialItems={specialItems}
            open={drawerOpen}
            handleOpen={handleOpen}
          />
        )}
        {isSearchBoxOpen && <OverflowHidden />}
        {isSearchBoxOpen && <Overlay onClick={handleSearchBoxClose} />}
        <TopNavStrip icons={topStrip} />
        <HeaderWrapper ref={containerRef} isIndex={isIndex ?? false}>
          <Header
            categoryOutOfView={categoryOutOfView}
            displayNavbar={displayNavbar}
          >
            <Container ref={headerRef}>
              <Nav>
                <AnimatedHeaderWrapper
                  isDesktopNavOpened={desktopNavOpen}
                  categoryOutOfView={categoryOutOfView}
                  toggleDesktopNav={toggleDesktopNav}
                  handleMouseleave={handleMouseleave}
                  active={active}
                  reset={reset}
                  categoryRef={categoryRef}
                  mainRef={mainRef}
                >
                  <NavRowContainer categoryOutOfView={categoryOutOfView}>
                    <NavSearchContainer data-is-active={isSearchBoxOpen}>
                      <NavHeader
                        value={value}
                        setValue={setValue}
                        handleSearchBox={setIsSearchBoxOpen}
                        searchBarData={searchBarData}
                        categoryOutOfView={categoryOutOfView}
                        desktopMenuClick={toggleDesktopNav(!desktopNavOpen)}
                        drawerToggleHandler={drawerToggleHandler}
                        suggestedMenus={suggestedMenus}
                      />
                      <MobileNavRow
                        value={value}
                        setValue={setValue}
                        handleSearchBox={setIsSearchBoxOpen}
                        searchBarData={searchBarData}
                        suggestedMenus={suggestedMenus}
                        drawerToggleHandler={drawerToggleHandler}
                      />
                    </NavSearchContainer>
                    {isSearchBoxOpen && (
                      <SearchBox
                        isLoading={searchBarData?.isLoading}
                        suggestedBrands={searchBarData?.suggestedBrands}
                        suggestedCategories={searchBarData?.suggestedCategories}
                        suggestions={searchBarData?.suggestions}
                        searchValue={value}
                        onClose={handleSearchBoxClose}
                        onSearchSubmit={searchBarData.onSearchSubmit}
                        top={{ desktop: '-2rem', mobile: '6rem' }}
                        {...searchBox}
                      ></SearchBox>
                    )}
                  </NavRowContainer>
                </AnimatedHeaderWrapper>
                <Container className="container">
                  <MenuCategories
                    items={categoryItems.nodes as BaseMenuItem[]}
                    inView={inView}
                    stripInView={stripInView}
                    isIndex={isIndex ?? false}
                    categoryRef={categoryRef}
                    open={desktopNavOpen}
                    categoryOutOfView={categoryOutOfView}
                    toggleDesktopNav={toggleDesktopNav}
                    absoluteRef={absoluteRef}
                    handleMouseleave={handleMouseleave}
                    active={active}
                    reset={reset}
                  />
                </Container>
              </Nav>
            </Container>
          </Header>
        </HeaderWrapper>
        <FullWidthWrapper isIndex={isIndex}>
          <Container className="container">
            <NavItems ref={stripRef}>
              <MenuStrip
                items={menuItems}
                desktopMenuClick={toggleDesktopNav(!desktopNavOpen)}
                categoryRef={categoryRef}
                handleMouseleave={handleMouseleave}
                toggleDesktopNav={toggleDesktopNav}
              />
            </NavItems>
          </Container>
        </FullWidthWrapper>
        <Container className="container">
          <Link href={mainBannerUrl} passHref>
            <Banner ref={ref} categoryOutOfView={!inView} isIndex={isIndex}>
              {categoryItems.menuImage && (
                <LazyImage
                  src={categoryItems.menuImage as string}
                  key={categoryItems.menuImage as string}
                  width={1440}
                  height={540}
                  alt={labels?.ariaLabelsAltsPlaceholders?.mainBanner}
                />
              )}
            </Banner>
          </Link>
        </Container>
      </>
    </LocalLabelsProvider>
  )
}

const NavSearchContainer = styled.div.attrs({})`
  &[data-is-active='true'] {
    z-index: 9999;
    background: #fff;
    left: 50%;
  }
`

type HeaderType = {
  categoryOutOfView?: boolean
  displayNavbar?: boolean
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`

// Component to be a placeholder for the Navigation
const HeaderWrapper = styled.div<{ isIndex?: boolean }>`
  position: relative;
  z-index: 200;
  height: 12.25rem;
  background-color: ${({ theme }) => theme.palette.secondary};

  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    height: 9.4rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    height: 9.4rem;
  }
`

const Header = styled.header<HeaderType>`
  z-index: 1999;
  position: ${(props) => (props.categoryOutOfView ? 'fixed' : 'absolute')};
  left: 0;
  width: 100%;
  top: 0;

  pointer-events: ${(props) =>
    !(props.categoryOutOfView && !props.displayNavbar) ? 'auto' : 'none'};

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    top: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm.max}) {
    position: fixed;
  }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  z-index: 9;

  opacity: 1;
  visibility: visible;
  transition: opacity 0.8s ease, visibility 0s;
`

const NavRowContainer = styled.div<HeaderType>`
  position: relative;
  top: ${(props) => (props.categoryOutOfView ? '0' : '12.25rem')};
  z-index: 10;
`

const Nav = styled.nav.attrs({
  className: 'mx-auto',
})`
  height: 100%;
  position: relative;

  & > div {
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm.max}) {
    width: 100%;
    padding: 0;
  }
`

const FullWidthWrapper = styled.div<{ isIndex?: boolean }>`
  width: 100%;
  color: ${({ theme }) => theme.palette.silverTree};
  background-color: ${({ theme }) => theme.palette.secondary};

  position: relative;
  z-index: 100;

  box-shadow: 0 0 0.8rem #e6e5e5;
`

type BannerType = { categoryOutOfView: boolean; isIndex?: boolean }

const Banner = styled.a<BannerType>`
  width: calc(100% + 1rem);
  background: ${({ theme }) => theme.palette.secondary};
  display: flex;
  position: relative;
  box-shadow: 0 0.2rem 1rem -0.2rem rgba(0, 0, 0, 0.15);
  margin-left: -1rem;

  overflow: hidden;

  & > div {
    margin-left: 27.2rem;
  }

  & > div > img {
    object-fit: cover;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    display: none;
  }

  ${(props) =>
    !props.isIndex &&
    css`
      display: none;
    `}
`

const NavItems = styled.div`
  height: 4.6rem;
  display: flex;
  width: 100%;
  align-items: center;
  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    display: none;
  }
`
